import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { listGateProgress, type GateProgressRow } from "@/lib/gates.functions";
import { getJourneyState } from "@/lib/journey.functions";
import { TempleScene } from "@/components/site/TempleScene";
import { ConsciousnessGarden } from "@/components/site/ConsciousnessGarden";
import { Card } from "@/components/ui/card";

export const Route = createFileRoute("/_authenticated/journey/temple")({
  head: () => ({
    meta: [
      { title: "Temple of Consciousness · KAfrequency" },
      {
        name: "description",
        content:
          "A living sanctuary that emerges as you walk the 47 Gates — lanterns, gardens, meditation hall, library, and the inner sanctum.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TemplePage,
});

function TemplePage() {
  const loadProgress = useServerFn(listGateProgress);
  const loadJourney = useServerFn(getJourneyState);
  const [progress, setProgress] = useState<GateProgressRow[] | null>(null);
  const [journey, setJourney] = useState<Awaited<ReturnType<typeof getJourneyState>> | null>(null);
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    loadProgress().then(setProgress).catch(() => setProgress([]));
    loadJourney().then(setJourney).catch(() => setJourney(null));
  }, [loadProgress, loadJourney]);

  const completedCount = (progress ?? []).filter((p) => p.completed_at).length;
  const vitality = journeyVitality(journey);

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Symbolic sanctuary</p>
        <h2 className="mt-2 font-serif text-2xl text-gold-luminous sm:text-3xl">Temple of Consciousness</h2>
        <p className="mt-2 max-w-2xl text-sm text-foreground/70">
          Your temple grows as you walk the 47 Gates. Elements appear quietly as clusters complete — a
          lantern first, then the path, garden, hall, library, chamber, observatory, and inner sanctum.
        </p>
      </header>

      <TempleScene completedCount={completedCount} reduceMotion={reduceMotion} />

      <Card className="p-4">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">The garden</p>
        <div className="mt-3">
          <ConsciousnessGarden
            vitality={vitality}
            caption="Practice waters the garden. If you pause, growth pauses too — nothing dies."
          />
        </div>
      </Card>

      <div className="flex items-center justify-between rounded-md border hairline p-4 text-sm">
        <span className="text-foreground/70">Continue walking the Gates to reveal more of the temple.</span>
        <Link to="/journey/map" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
          View the map →
        </Link>
      </div>
    </div>
  );
}

function journeyVitality(state: Awaited<ReturnType<typeof getJourneyState>> | null): number {
  if (!state) return 0;
  // Signals: last 30 days of completions + pinned/active challenges
  const now = Date.now();
  const THIRTY = 30 * 24 * 60 * 60 * 1000;
  const recentCompletions = (state.completions ?? []).filter((c) => {
    const t = c.completed_at ? new Date(c.completed_at).getTime() : 0;
    return t > 0 && now - t <= THIRTY;
  }).length;
  // Score: 0..1 saturating around ~15 recent completions
  return Math.min(1, recentCompletions / 15);
}
